"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import {
  Menu,
  X,
  LayoutDashboard,
  Calendar,
  User as UserIcon,
  LogOut,
  ChevronDown,
  FileText,
  HelpCircle,
  Search,
} from "lucide-react";
import { useCustomerContext } from "@/features/customer/context/CustomerContext";
import { getAuthToken, clearAuthToken, logoutUser } from "@/lib/auth-token";
import { formatStorageUrl } from "@/lib/api-config";

const NAV_LINKS = [
  { href: "/offers", label: "Offers" },
  { href: "/fleet", label: "Fleet" },
  { href: "/about-us", label: "About Us" },
  { href: "/contact", label: "Contact Us" },
  { href: "/faq", label: "FAQ's" },
];

export function IKHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  const { profile, setProfile } = useCustomerContext();
  const isLoggedIn = isMounted && Boolean(profile && getAuthToken());

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setSearchString(window.location.search);
    }
  }, [pathname]);

  if (pathname?.startsWith("/customer")) {
    return null;
  }

  const getNavLinkClass = (href: string) => {
    const isActive = pathname === href;
    return `text-sm font-semibold transition-colors ${
      isActive
        ? "text-[#FFB51D]"
        : "text-[#131222] dark:text-white hover:text-[#FFB51D]"
    }`;
  };

  const getMobileLinkClass = (href: string) => {
    const isActive = pathname === href;
    return `block py-3 px-2 rounded-lg ${
      isActive
        ? "bg-[#FFB51D]/10 text-[#FFB51D] font-bold transition-colors"
        : "text-[#131222] dark:text-white font-medium hover:text-[#FFB51D] transition-colors"
    }`;
  };

  const fullPath = pathname ? (searchString ? `${pathname}${searchString}` : pathname) : "/";

  const loginHref =
    fullPath && !fullPath.startsWith("/login") && !fullPath.startsWith("/signup")
      ? `/login?returnTo=${encodeURIComponent(fullPath)}`
      : "/login";

  const handleLogout = async () => {
    setDropdownOpen(false);
    setMobileOpen(false);
    await logoutUser();
    setProfile(null);

    // Only redirect to login if logging out from a protected route
    const isProtectedRoute =
      pathname?.startsWith("/customer") ||
      pathname?.startsWith("/agent") ||
      pathname?.startsWith("/driver");

    if (isProtectedRoute) {
      router.replace("/login");
    }
  };

  const initialLetter = profile?.name && profile.name.trim() ? profile.name.trim()[0].toUpperCase() : "";

  return (
    <header className="sticky top-0 z-[200] w-full border-b border-[#FFB51D]/10 dark:border-neutral-700 bg-[#fff8f0]/95 dark:bg-neutral-950/70 backdrop-blur-md supports-[backdrop-filter]:bg-[#fff8f0]/90 dark:supports-[backdrop-filter]:bg-neutral-950/60">
      <nav
        className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8"
        aria-label="Main navigation"
      >
        <Link href="/" className="flex shrink-0">
          <Image
            src="/images/resources/logo-1.png"
            alt="ik travel"
            width={180}
            height={52}
            priority
            loading="eager"
            className="h-10 w-auto sm:h-12"
          />
        </Link>

        <ul className="hidden lg:flex items-center gap-6 list-none m-0 p-0">
          {NAV_LINKS.map(({ href, label }) => (
            <li key={href}>
              <Link href={href} className={getNavLinkClass(href)}>
                {label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          {isLoggedIn ? (
            <div className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="flex items-center gap-2 p-1.5 pr-3 rounded-full bg-white dark:bg-neutral-900 border border-[#F0EBE1] dark:border-neutral-800 hover:bg-[#FAF7F2] dark:hover:bg-neutral-800 transition-colors shadow-2xs cursor-pointer"
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                {profile?.avatar_url ? (
                  <img
                    src={formatStorageUrl(profile.avatar_url)}
                    alt={profile.name || "User Avatar"}
                    className="h-9 w-9 rounded-full object-cover border border-[#FFB51D]/40 shrink-0"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-[#E7E5E4] dark:bg-neutral-800 text-[#44403C] dark:text-neutral-200 font-bold text-sm flex items-center justify-center border border-[#D6D3D1] dark:border-neutral-700 shrink-0">
                    {initialLetter || <UserIcon className="h-4 w-4 text-[#78716C]" />}
                  </div>
                )}
                {profile?.name && (
                  <span className="text-xs font-extrabold text-[#131222] dark:text-white leading-tight max-w-[120px] truncate">
                    {profile.name}
                  </span>
                )}
                <ChevronDown className={`h-3.5 w-3.5 text-[#A8A29E] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[200]"
                    onClick={() => setDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white dark:bg-neutral-900 border border-[#F0EBE1] dark:border-neutral-800 shadow-2xl py-2 z-[210] animate-in fade-in slide-in-from-top-2">
                    {profile?.name && (
                      <div className="px-4 py-2 border-b border-[#F0EBE1] dark:border-neutral-800">
                        <p className="text-xs font-extrabold text-[#131222] dark:text-white truncate">
                          {profile.name}
                        </p>
                        {profile.mobile && (
                          <p className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                            {profile.mobile}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="py-1">
                      <Link
                        href="/customer"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:text-[#FFB51D] hover:bg-[#FFB51D]/10 transition-colors"
                      >
                        <LayoutDashboard className="h-4 w-4 text-[#FFB51D]" />
                        <span>Dashboard</span>
                      </Link>

                      <Link
                        href="/customer/book-vehicle"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:text-[#FFB51D] hover:bg-[#FFB51D]/10 transition-colors"
                      >
                        <Search className="h-4 w-4 text-[#FFB51D]" />
                        <span>Book Vehicle ⭐</span>
                      </Link>

                      <Link
                        href="/customer/bookings"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:text-[#FFB51D] hover:bg-[#FFB51D]/10 transition-colors"
                      >
                        <Calendar className="h-4 w-4 text-[#FFB51D]" />
                        <span>My Bookings</span>
                      </Link>

                      <Link
                        href="/customer/documents"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:text-[#FFB51D] hover:bg-[#FFB51D]/10 transition-colors"
                      >
                        <FileText className="h-4 w-4 text-[#FFB51D]" />
                        <span>Documents</span>
                      </Link>

                      <Link
                        href="/customer/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:text-[#FFB51D] hover:bg-[#FFB51D]/10 transition-colors"
                      >
                        <UserIcon className="h-4 w-4 text-[#FFB51D]" />
                        <span>Profile</span>
                      </Link>

                      <Link
                        href="/customer/support"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:text-[#FFB51D] hover:bg-[#FFB51D]/10 transition-colors"
                      >
                        <HelpCircle className="h-4 w-4 text-[#FFB51D]" />
                        <span>Support & Help</span>
                      </Link>
                    </div>

                    <div className="pt-1 border-t border-[#F0EBE1] dark:border-neutral-800">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 w-full transition-colors cursor-pointer"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link
              href={loginHref}
              className="hidden sm:inline-flex items-center justify-center rounded-lg border-2 border-[#FFB51D] bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 dark:border-[#FFB51D] px-4 py-2 text-sm font-medium text-[#131222] dark:text-white hover:bg-[#FFB51D] hover:text-[#131222] transition-colors min-h-[44px]"
            >
              Login or Sign Up
            </Link>
          )}

          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden inline-flex items-center justify-center rounded-md min-w-[44px] min-h-[44px] text-[#131222] dark:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-[#FFB51D] transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#FFB51D]/10 dark:border-neutral-700 bg-[#fff8f0] dark:bg-neutral-950 px-4 py-4 space-y-4">
          {isLoggedIn ? (
            <>
              {/* 1. User Profile & Account Actions (FIRST AT TOP) */}
              <div className="rounded-2xl border border-[#F0EBE1] dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3.5 shadow-sm space-y-3">
                {profile?.name && (
                  <div className="flex items-center gap-3 px-1 py-1 border-b border-neutral-100 dark:border-neutral-800 pb-2.5">
                    {profile.avatar_url ? (
                      <img
                        src={formatStorageUrl(profile.avatar_url)}
                        alt={profile.name}
                        className="h-10 w-10 rounded-full object-cover border-2 border-[#FFB51D]"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-[#FFB51D] text-[#131222] font-black text-sm flex items-center justify-center shrink-0 shadow-2xs">
                        {profile.name[0].toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-black text-[#131222] dark:text-white truncate">
                        {profile.name}
                      </p>
                      {profile.mobile && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                          {profile.mobile}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-1 pt-1">
                  <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-[#FFB51D]">
                    Customer Menu
                  </span>

                  <Link
                    href="/customer"
                    className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-xl text-xs font-bold text-[#131222] dark:text-white hover:bg-[#FFB51D]/15 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <LayoutDashboard className="h-4 w-4 text-[#FFB51D]" />
                    <span>Dashboard</span>
                  </Link>

                  <Link
                    href="/customer/book-vehicle"
                    className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-xl text-xs font-bold text-[#131222] dark:text-white hover:bg-[#FFB51D]/15 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <Search className="h-4 w-4 text-[#FFB51D]" />
                    <span>Book Vehicle ⭐</span>
                  </Link>

                  <Link
                    href="/customer/bookings"
                    className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-xl text-xs font-bold text-[#131222] dark:text-white hover:bg-[#FFB51D]/15 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <Calendar className="h-4 w-4 text-[#FFB51D]" />
                    <span>My Bookings</span>
                  </Link>

                  <Link
                    href="/customer/documents"
                    className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-xl text-xs font-bold text-[#131222] dark:text-white hover:bg-[#FFB51D]/15 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <FileText className="h-4 w-4 text-[#FFB51D]" />
                    <span>Documents</span>
                  </Link>

                  <Link
                    href="/customer/profile"
                    className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-xl text-xs font-bold text-[#131222] dark:text-white hover:bg-[#FFB51D]/15 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <UserIcon className="h-4 w-4 text-[#FFB51D]" />
                    <span>Profile</span>
                  </Link>

                  <Link
                    href="/customer/support"
                    className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-xl text-xs font-bold text-[#131222] dark:text-white hover:bg-[#FFB51D]/15 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <HelpCircle className="h-4 w-4 text-[#FFB51D]" />
                    <span>Support & Help</span>
                  </Link>

                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-xl text-xs font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors cursor-pointer"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>

              {/* 2. Public Website Pages (SECOND / BELOW CLEAR SEPARATOR) */}
              <div className="pt-1">
                <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                  Explore Pages
                </span>
                <ul className="flex flex-col gap-1 list-none m-0 p-0 mt-1.5">
                  {NAV_LINKS.map(({ href, label }) => (
                    <li key={href}>
                      <Link
                        href={href}
                        className={getMobileLinkClass(href)}
                        onClick={() => setMobileOpen(false)}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <>
              <ul className="flex flex-col gap-1 list-none m-0 p-0">
                {NAV_LINKS.map(({ href, label }) => (
                  <li key={href}>
                    <Link
                      href={href}
                      className={getMobileLinkClass(href)}
                      onClick={() => setMobileOpen(false)}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <Link
                  href={loginHref}
                  className="block py-3 px-2 rounded-xl border-2 border-[#FFB51D] bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 dark:border-[#FFB51D] text-center font-bold text-xs text-[#131222] dark:text-white hover:bg-[#FFB51D] hover:text-[#131222] transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Login or Sign Up
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
}
